import React from 'react';
import { mergeClassNames } from '@givelify/utils';
import { SnackbarCloseReason } from '@mui/material';
import { GivelifyButton } from '../button';
import { GivelifyIcon, GivelifyIconVariant } from '../icon';
import { GivelifyLabel } from '../label';
import { StyledAction, StyledMessage, StyledSnackbar } from './styles';
import { GivelifyNotificationProps } from './types';

export const GivelifyNotification: React.FCC<GivelifyNotificationProps> = ({
    variant,
    text,
    description,
    showDismiss,
    dismissLabel,
    customMessage,
    actionButtonProps,
    global,
    onClose,
    icon,
    ...props
}) => {
    const onDismiss = React.useCallback(
        (event: React.MouseEvent<HTMLButtonElement>) => {
            if (onClose) {
                onClose(event, 'dismiss');
            }
        },
        [onClose],
    );
    const innerClose = React.useCallback(
        (
            event: React.SyntheticEvent<any> | Event,
            reason: SnackbarCloseReason | 'dismiss',
        ) => {
            if (onClose) {
                onClose(event, reason);
            }
        },
        [onClose],
    );
    const action = React.useMemo(
        () =>
            actionButtonProps || showDismiss ? (
                <StyledAction data-testid="gvl-ntf-action-root">
                    {actionButtonProps ? (
                        <GivelifyButton
                            data-testid="gvl-ntf-action-btn"
                            {...actionButtonProps}
                            className={mergeClassNames(
                                'ntf-act',
                                !showDismiss ||
                                    actionButtonProps.variant === 'primary'
                                    ? `ntf-act-prm-${variant}`
                                    : `ntf-act-sec-${variant}`,
                                actionButtonProps.className,
                            )}
                            text={actionButtonProps.text || ''}
                            variant={
                                !showDismiss ||
                                actionButtonProps.variant === 'primary'
                                    ? 'primary'
                                    : 'secondary'
                            }
                        />
                    ) : null}
                    {showDismiss ? (
                        <GivelifyButton
                            disableRipple
                            className="ntf-act-dismiss"
                            data-testid="gvl-ntf-action-dismiss"
                            name="Dismiss"
                            onClick={onDismiss}
                            size="small"
                            text={dismissLabel ? dismissLabel : 'Dismiss'}
                            variant="ghost"
                        />
                    ) : null}
                </StyledAction>
            ) : null,
        [actionButtonProps, showDismiss, variant, dismissLabel, onDismiss],
    );
    const iconVariant: GivelifyIconVariant = React.useMemo(
        () =>
            icon
                ? icon
                : variant === 'success'
                ? 'success-box-icon'
                : variant === 'error'
                ? 'error-box-icon'
                : variant === 'info'
                ? 'info-box-icon'
                : 'warning-box-icon',
        [variant, icon],
    );
    const message = React.useMemo(
        () => (
            <StyledMessage
                data-testid="gvl-ntf-message-root"
                noActions={actionButtonProps === undefined && !showDismiss}
            >
                <GivelifyLabel
                    className="ntf-title"
                    data-testid="gvl-ntf-text"
                    variant="body2B"
                >
                    <GivelifyIcon className="ntf-icon" variant={iconVariant} />
                    {text}
                </GivelifyLabel>
                {description ? (
                    typeof description === 'string' ? (
                        <GivelifyLabel
                            className="ntf-content"
                            data-testid="gvl-ntf-description"
                            variant="body2"
                        >
                            {description}
                        </GivelifyLabel>
                    ) : (
                        description
                    )
                ) : null}
            </StyledMessage>
        ),
        [actionButtonProps, showDismiss, iconVariant, text, description],
    );
    return (
        <StyledSnackbar
            {...props}
            action={customMessage ? undefined : action}
            contentFullwidth={customMessage ? true : false}
            data-testid="gvl-ntf-root"
            global={global}
            message={customMessage ? customMessage : message}
            onClose={innerClose}
            variant={variant}
        />
    );
};
